// The acting tenant the client remembers between visits (Phase 1 / T1.6A).
//
// `pb.active` v2 holds `{ version: 2, businessId, locationId }` — the pair the
// canonical URLs need. It replaces `pb.activeBusiness`, which held a bare SITE
// id from before the tenancy rename.
//
// IT IS A PREFERENCE, NEVER AN AUTHORIZATION. Storage is writable by anyone at
// the keyboard, so a stored pair is only ever used after it has been matched
// against the ACTIVE memberships and in-scope Locations the server just
// returned. A pair that does not match is discarded, not repaired: we would
// rather send the owner to a business they can definitely use than to a URL
// that 404s, and we must never let a value someone typed into localStorage
// decide which tenant a request acts on.
export const ACTIVE_TENANT_KEY = 'pb.active';
/** v1: a bare SITE/Location id. Migrated once, through the server, then removed. */
export const LEGACY_ACTIVE_KEY = 'pb.activeBusiness';

export const ACTIVE_TENANT_VERSION = 2;

export interface ActiveTenant {
  businessId: string;
  locationId: string;
}

interface StoredV2 extends ActiveTenant {
  version: number;
}

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0;
}

/** Parse a raw stored string. Anything unexpected is null — never a partial. */
export function parseActiveTenant(raw: string | null): ActiveTenant | null {
  if (!raw) return null;
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Partial<StoredV2>;
  if (o.version !== ACTIVE_TENANT_VERSION) return null;
  if (!isNonEmptyString(o.businessId) || !isNonEmptyString(o.locationId)) return null;
  return { businessId: o.businessId, locationId: o.locationId };
}

export function readActiveTenant(): ActiveTenant | null {
  try {
    return parseActiveTenant(localStorage.getItem(ACTIVE_TENANT_KEY));
  } catch {
    // Private mode / storage disabled: no remembered tenant, which is a valid
    // state — resolution falls through to the authorized default.
    return null;
  }
}

export function writeActiveTenant(t: ActiveTenant): void {
  try {
    localStorage.setItem(
      ACTIVE_TENANT_KEY,
      JSON.stringify({ version: ACTIVE_TENANT_VERSION, businessId: t.businessId, locationId: t.locationId }),
    );
  } catch {
    // Same fallback: the in-memory navigation still works for this session.
  }
}

export function clearActiveTenant(): void {
  try {
    localStorage.removeItem(ACTIVE_TENANT_KEY);
  } catch {
    /* nothing to clear */
  }
}

/** The legacy v1 value, if one is still there. */
export function readLegacyActive(): string | null {
  try {
    const raw = localStorage.getItem(LEGACY_ACTIVE_KEY);
    return isNonEmptyString(raw) ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Retire the legacy key. Called on EVERY outcome of the migration — success,
 * a foreign or deleted Site, a malformed value — so the lookup runs at most
 * once per browser and a dead id can never drive an endless retry.
 */
export function clearLegacyActive(): void {
  try {
    localStorage.removeItem(LEGACY_ACTIVE_KEY);
  } catch {
    /* nothing to clear */
  }
}
